<template>
  <div class="template-viewer" ref="rootRef">
    <div v-if="!hideHeader" class="template-viewer-header">
      <div class="header-left">
        <div class="header-text">
          <h6 class="header-title">Template Viewer</h6>
          <p class="header-subtitle">Browse and copy templates</p>
        </div>
      </div>
      <button type="button" class="header-close-btn" @click="$emit('close')" aria-label="Close">
        <Icon icon="mdi:close" width="18" height="18" />
      </button>
    </div>

    <div class="filter-section">
      <div class="filter-tabs-wrapper">
        <button
          v-if="canScrollFilterLeft"
          class="filter-scroll-btn left"
          @click="scrollFiltersLeft"
        >
          <Icon icon="mdi:chevron-left" width="16" height="16" />
        </button>
        <div class="filter-tabs" ref="filterTabsContainer">
          <button
            v-for="filter in filters"
            :key="filter.value"
            :class="['filter-tab', { active: selectedFilter === filter.value }]"
            @click="selectMainFolder(filter)"
          >
            <Icon :icon="filter.icon" width="14" height="14" />
            <span>{{ filter.label }}</span>
          </button>
        </div>
        <button
          v-if="canScrollFilterRight"
          class="filter-scroll-btn right"
          @click="scrollFiltersRight"
        >
          <Icon icon="mdi:chevron-right" width="16" height="16" />
        </button>
      </div>

      <div v-if="selectedFilter === 'All'" class="search-container">
        <Input
          v-model="searchQuery"
          type="search"
          placeholder="Search templates..."
          leadingIcon="mdi:magnify"
          size="sm"
          class="search-input-custom"
        />
      </div>
    </div>

    <div class="templates-container-wrapper">
      <div
        v-if="(isLoadingMainFolders && filters.length === 1) || isLoadingSubFolders"
        class="loading-overlay"
      >
        <LoadingBars size="md" />
      </div>

      <template v-else>
        <div v-if="selectedFilter !== 'All'" class="sub-filter-container">
          <div class="sub-filter-tabs">
            <button
              v-for="subFilter in getSubFilters(selectedMainFolderId)"
              :key="subFilter.id || 'all'"
              :class="['sub-filter-tab', { active: selectedSubFilter === subFilter.value }]"
              @click="selectSubFolder(subFilter)"
            >
              {{ subFilter.label }}
            </button>
          </div>
        </div>

        <div class="templates-container">
          <div v-if="isLoadingTemplates" class="loading-overlay">
            <LoadingBars size="md" :show-text="false" />
          </div>

          <template v-else>
            <div v-if="filteredTemplates.length === 0" class="empty-container">
              <div class="empty-icon-wrapper">
                <Icon icon="mdi:file-document-outline" width="28" height="28" class="empty-icon" />
              </div>
              <div class="empty-text">
                <p class="empty-title">No templates available</p>
                <p class="empty-description">Create templates to see them here.</p>
              </div>
            </div>

            <div v-else class="templates-list">
              <article
                v-for="template in filteredTemplates"
                :key="template.id"
                class="template-card"
                @click="openPreview(template)"
              >
                <div class="template-card__header">
                  <span class="template-card__category">{{ getCategoryLabel(template) }}</span>
                  <h3 class="template-card__name">{{ template.name }}</h3>
                </div>
                <p class="template-card__subject">{{ template.subject }}</p>
                <div
                  class="template-card__content"
                  v-html="truncateContent(template.content)"
                ></div>
                <div class="template-card__footer">
                  <div class="template-card__stats">
                    <button
                      type="button"
                      class="template-card__stat template-card__stat--positive"
                      @click.stop="markPositive(template)"
                      title="Mark Positive"
                    >
                      <Icon icon="mdi:thumb-up" width="14" height="14" />
                      <span>{{ template.positiveReplies || 0 }}</span>
                    </button>
                    <button
                      type="button"
                      class="template-card__stat template-card__stat--negative"
                      @click.stop="markNegative(template)"
                      title="Mark Negative"
                    >
                      <Icon icon="mdi:thumb-down" width="14" height="14" />
                      <span>{{ template.negativeReplies || 0 }}</span>
                    </button>
                  </div>
                  <Button
                    variant="primary"
                    size="xs"
                    icon="mdi:content-copy"
                    icon-position="left"
                    class="template-card__copy-btn"
                    @click.stop="copyTemplate(template)"
                    title="Copy template"
                  >
                    Copy ({{ template.copied || 0 }})
                  </Button>
                </div>
              </article>
            </div>
          </template>
        </div>
      </template>
    </div>

    <Teleport to="body">
      <TemplatePreviewModal
        :show="!!previewTemplate"
        :template="previewTemplate"
        :anchor-left="previewAnchorLeft"
        @close="closePreview"
        @mark-positive="markPositive"
        @mark-negative="markNegative"
        @copy-template="copyTemplate"
        @edit-template="editTemplate"
        @delete-template="deleteTemplate"
      />
    </Teleport>

    <EditTemplateModal
      v-if="selectedTemplate"
      :selectedTemplate="selectedTemplate"
      @closeEditTemplateModal="closeEditModal"
      @refreshTemplates="refetchTemplates"
    />

    <DeleteConfirmationModal
      v-if="showDeleteModal && templateToDelete"
      :show="showDeleteModal"
      :deleteFunction="() => deleteTemplateFunction(templateToDelete)"
      entity="Template"
      :plural="false"
      @closeDeleteConfirmationModal="cancelDelete"
      @refreshRecords="refetchTemplates"
    />
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useToast } from "@/composables/ui/useToast";
import { useStore } from "vuex";
import { Icon } from "@iconify/vue";
import { Button, Input } from "@/components/ui";
import LoadingBars from "@/components/ui/LoadingBars.vue";
import DeleteConfirmationModal from "../modal/DeleteConfirmationModal.vue";
import EditTemplateModal from "./EditTemplateModal.vue";
import TemplatePreviewModal from "./TemplatePreviewModal.vue";
import {
  useTemplates,
  useParentTemplateFolders,
  useTemplateFolders,
  useCopyTemplate,
  useMarkTemplatePositive,
  useMarkTemplateNegative,
  useDeleteTemplate,
} from "@/composables/templates";
defineProps({
  hideHeader: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(["close", "preview-change"]);
const store = useStore();
const $toast = useToast();

const FOLDER_ICON_MAP = {
  messages: "mdi:email-outline",
  call_script: "mdi:phone-outline",
  linkedin: "mdi:linkedin",
  others: "mdi:file-document-outline",
};

// State
const selectedFilter = ref("All");
const selectedSubFilter = ref("All");
const selectedMainFolderId = ref(null);
const selectedSubFolderId = ref(null);
const previewTemplate = ref(null);
const rootRef = ref(null);
const previewAnchorLeft = ref(null);
const searchQuery = ref("");
const filterTabsContainer = ref(null);
const canScrollFilterLeft = ref(false);
const canScrollFilterRight = ref(false);
const selectedTemplate = ref(null);
const showDeleteModal = ref(false);
const templateToDelete = ref(null);

// Account data
const account = computed(() => store.getters.account.data);
const organizationId = computed(() => account.value?.organizationId);

// Parent template folders
const parentTemplateFoldersParams = computed(() => {
  if (!organizationId.value) return null;
  return {
    organizationId: organizationId.value,
  };
});

const { data: parentTemplateFoldersData, isLoading: isLoadingMainFolders } =
  useParentTemplateFolders(parentTemplateFoldersParams);

const parentTemplateFolders = computed(() => parentTemplateFoldersData.value || []);

// Template folders
const templateFoldersParams = computed(() => {
  if (!organizationId.value) return null;
  return {
    organizationId: organizationId.value,
    parentTemplateFolderId: selectedMainFolderId.value || undefined,
  };
});

const { data: templateFoldersData, isLoading: isLoadingSubFolders } =
  useTemplateFolders(templateFoldersParams);

const templateFolders = computed(() => templateFoldersData.value || []);

// Templates
const templatesParams = computed(() => {
  if (!organizationId.value) return null;
  return {
    organizationId: organizationId.value,
    limit: 100,
  };
});

const {
  data: templatesData,
  isLoading: isLoadingTemplates,
  refetch: refetchTemplates,
} = useTemplates(templatesParams);

const templates = computed(() => templatesData.value?.data || []);

// Filters
const filters = computed(() => {
  const mainFolders = [{ value: "All", label: "All", icon: "mdi:view-grid-outline", id: null }];

  if (parentTemplateFolders.value?.length) {
    parentTemplateFolders.value.forEach((folder) => {
      mainFolders.push({
        value: folder.name,
        label: folder.name,
        icon: FOLDER_ICON_MAP[folder.icon] || "mdi:folder-outline",
        id: folder.id,
      });
    });
  }

  return mainFolders;
});

function getSubFilters(mainFolderId) {
  if (!mainFolderId || mainFolderId === "All") return [];
  const subFolders = [{ value: "All", label: "All", id: null }];
  const filtered =
    templateFolders.value?.filter((f) => f.parentTemplateFolderId === mainFolderId) ?? [];
  filtered.forEach((folder) => {
    subFolders.push({ value: folder.name, label: folder.name, id: folder.id });
  });
  return subFolders;
}

// Filtered templates
const filteredTemplates = computed(() => {
  let result = templates.value;

  if (selectedFilter.value === "All") {
    if (searchQuery.value.trim()) {
      const query = searchQuery.value.toLowerCase();
      result = result.filter(
        (t) =>
          t.name?.toLowerCase().includes(query) ||
          t.subject?.toLowerCase().includes(query) ||
          t.content?.toLowerCase().includes(query)
      );
    }
    return result;
  }

  if (selectedMainFolderId.value) {
    if (selectedSubFolderId.value) {
      result = result.filter((t) => t.templateFolderId === selectedSubFolderId.value);
    } else {
      const subFolderIds = getSubFilters(selectedMainFolderId.value)
        .filter((sf) => sf.id !== null)
        .map((sf) => sf.id);

      result = result.filter((t) => subFolderIds.includes(t.templateFolderId));
    }
  }

  return result;
});

function getCategoryLabel(template) {
  if (!template.templateFolderId) return template.category || "General";
  const subFolder = templateFolders.value.find((f) => f.id === template.templateFolderId);
  const mainFolder = subFolder
    ? parentTemplateFolders.value.find((f) => f.id === subFolder.parentTemplateFolderId)
    : null;
  return mainFolder?.name ?? template.category ?? "General";
}

function truncateContent(html) {
  const div = document.createElement("div");
  div.innerHTML = html;
  const text = div.textContent || div.innerText || "";
  return text.length > 80 ? `${text.substring(0, 80)}...` : text;
}

const copyTemplateMutation = useCopyTemplate();
const markPositiveMutation = useMarkTemplatePositive();
const markNegativeMutation = useMarkTemplateNegative();
const deleteTemplateMutation = useDeleteTemplate();

function openPreview(template) {
  // Anchor the detail panel relative to wherever this panel actually is on
  // screen (it can be the wide inline left column or the narrower floating
  // panel), offset partway across its own width so it overlaps it — matching
  // the requested layout — rather than a hardcoded pixel guess that drifts
  // off depending on viewport size.
  const rect = rootRef.value?.getBoundingClientRect();
  previewAnchorLeft.value = rect ? Math.round(rect.left + rect.width * 0.45) : null;

  previewTemplate.value = template;
  emit("preview-change", true);
}

function closePreview() {
  previewTemplate.value = null;
  emit("preview-change", false);
}

function htmlToText(html) {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  tmp.querySelectorAll("br").forEach((br) => br.replaceWith(document.createTextNode("\n")));
  tmp.querySelectorAll("p, div, h1, h2, h3, h4, h5, h6").forEach((el) => {
    el.insertAdjacentText("afterend", "\n");
  });
  tmp.querySelectorAll("li").forEach((li) => li.insertAdjacentText("beforeend", "\n"));
  let text = (tmp.textContent || tmp.innerText || "")
    .replace(/[ \t]+/g, " ")
    .replace(/\r\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n");
  return text.trim();
}

async function copyTemplate(template) {
  const plainTextContent = htmlToText(template.content || "");
  const content = template.subject + "\n\n" + plainTextContent;
  navigator.clipboard.writeText(content);

  try {
    await copyTemplateMutation.mutateAsync(template.id);
    $toast.success("Template copied to clipboard!");
  } catch (error) {
    $toast.error(error.message || "Failed to copy template");
  }
}

async function markPositive(template) {
  try {
    await markPositiveMutation.mutateAsync({ id: template.id, operation: "inc" });
  } catch (error) {
    $toast.error(error.message || "Failed to mark template as positive");
  }
}

async function markNegative(template) {
  try {
    await markNegativeMutation.mutateAsync({ id: template.id, operation: "inc" });
  } catch (error) {
    $toast.error(error.message || "Failed to mark template as negative");
  }
}

function editTemplate(template) {
  selectedTemplate.value = template;
}

function closeEditModal() {
  selectedTemplate.value = null;
  if (previewTemplate.value) {
    const updatedTemplate = templates.value.find((t) => t.id === previewTemplate.value.id);
    if (updatedTemplate) {
      previewTemplate.value = updatedTemplate;
    }
  }
}

function deleteTemplate(template) {
  templateToDelete.value = template;
  showDeleteModal.value = true;
}

async function deleteTemplateFunction(template) {
  try {
    await deleteTemplateMutation.mutateAsync(template.id);
    $toast.success("Template deleted successfully!");
    closePreview();
    return true;
  } catch (error) {
    $toast.error(error.message || "Failed to delete template");
    throw error;
  }
}

function cancelDelete() {
  showDeleteModal.value = false;
  templateToDelete.value = null;
}

function checkFilterScroll() {
  if (!filterTabsContainer.value) return;
  const { scrollLeft, scrollWidth, clientWidth } = filterTabsContainer.value;
  canScrollFilterLeft.value = scrollLeft > 0;
  canScrollFilterRight.value = scrollLeft + clientWidth < scrollWidth - 1;
}

function scrollFiltersLeft() {
  filterTabsContainer.value?.scrollBy({ left: -150, behavior: "smooth" });
  setTimeout(checkFilterScroll, 300);
}

function scrollFiltersRight() {
  filterTabsContainer.value?.scrollBy({ left: 150, behavior: "smooth" });
  setTimeout(checkFilterScroll, 300);
}

function selectMainFolder(filter) {
  selectedFilter.value = filter.value;
  selectedMainFolderId.value = filter.id;
  selectedSubFilter.value = "All";
  selectedSubFolderId.value = null;
}

function selectSubFolder(subFilter) {
  selectedSubFilter.value = subFilter.value;
  selectedSubFolderId.value = subFilter.id;
}

watch(selectedFilter, () => {
  selectedSubFilter.value = "All";
  selectedSubFolderId.value = null;
});

watch(
  templates,
  (newTemplates) => {
    if (previewTemplate.value && newTemplates) {
      const updatedTemplate = newTemplates.find((t) => t.id === previewTemplate.value.id);
      if (updatedTemplate) {
        previewTemplate.value = updatedTemplate;
      }
    }
  },
  { deep: true }
);

onMounted(() => {
  setTimeout(() => {
    checkFilterScroll();
  }, 100);
  if (filterTabsContainer.value) {
    filterTabsContainer.value.addEventListener("scroll", checkFilterScroll);
  }
});

onBeforeUnmount(() => {
  if (filterTabsContainer.value) {
    filterTabsContainer.value.removeEventListener("scroll", checkFilterScroll);
  }
});
</script>

<style scoped>
.template-viewer {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #ffffff;
  overflow: hidden;
  position: relative;
}

.template-viewer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 20px;
  background: linear-gradient(90deg, #2d3bff 0%, #4f63ff 100%);
  border-radius: 12px 12px 0 0;
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.header-subtitle {
  margin: 0;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.85);
}

.header-title {
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  color: #fff;
  letter-spacing: -0.01em;
}

.header-close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: #fff;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.header-close-btn:hover {
  background: rgba(255, 255, 255, 0.35);
}

.templates-container-wrapper {
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 40px;
  background: #ffffff;
  z-index: 10;
}

.filter-section {
  border-bottom: 1px solid #e8e8e8;
  background: #ffffff;
}

.filter-tabs-wrapper {
  display: flex;
  align-items: center;
  position: relative;
  padding: 12px 16px 8px;
  border-bottom: 1px solid #f3f4f6;
}

.filter-scroll-btn {
  background: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.2s ease;
  flex-shrink: 0;
  z-index: 1;
}

.filter-scroll-btn:hover {
  background: #f3f4f6;
  color: #374151;
  border-color: #d1d5db;
}

.filter-scroll-btn.left {
  margin-right: 8px;
}

.filter-scroll-btn.right {
  margin-left: 8px;
}

.filter-tabs {
  display: flex;
  gap: 4px;
  overflow-x: auto;
  scroll-behavior: smooth;
  flex: 1;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.filter-tabs::-webkit-scrollbar {
  display: none;
}

.search-container {
  padding: 8px 16px 12px;
  background: #f9fafb;
}

.sub-filter-container {
  padding: 8px 16px 12px;
  background: #f9fafb;
  border-top: 1px solid #e8e8e8;
}

.sub-filter-tabs {
  display: flex;
  gap: 4px;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding-bottom: 2px;
}

.sub-filter-tabs::-webkit-scrollbar {
  height: 4px;
}

.sub-filter-tabs::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 2px;
}

.sub-filter-tabs::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 2px;
}

.sub-filter-tabs::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.sub-filter-tab {
  padding: 6px 12px;
  border: 1px solid #e8e8e8;
  background: #ffffff;
  border-radius: 16px;
  font-size: 10px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
}

.sub-filter-tab:hover {
  border-color: #3e3efe;
  color: #3e3efe;
  background: #f3f1ff;
}

.sub-filter-tab.active {
  background: #3e3efe;
  color: #ffffff;
  border-color: #3e3efe;
}

.search-input-custom :deep(.ui-input__field) {
  height: 32px !important;
  padding: 2px 6px;
  font-size: 10px;
}

.search-input-custom :deep(.ui-input__field--has-leading) {
  padding-left: 28px;
}

.search-input-custom :deep(.ui-input__leading) {
  left: 8px;
  width: 14px;
  height: 14px;
}

.filter-tab {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: 1px solid #e8e8e8;
  background: #ffffff;
  border-radius: 16px;
  font-size: 10px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
}

.filter-tab:hover {
  border-color: #3e3efe;
  color: #3e3efe;
  background: #f3f1ff;
}

.filter-tab.active {
  background: #3e3efe;
  color: #ffffff;
  border-color: #3e3efe;
}

.templates-container {
  flex: 1;
  overflow: hidden;
  position: relative;
  min-height: 0;
  border-top: 2px solid #e8e8e8;
  background: #ffffff;
}

.empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  width: 100%;
  gap: 16px;
  padding: 40px 24px;
}

.empty-icon-wrapper {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
  border: 1px solid #c7d2fe;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-icon {
  color: #6366f1;
}

.empty-text {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  text-align: center;
  max-width: 280px;
}

.empty-title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.empty-description {
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

.templates-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 16px;
  overflow-y: auto;
  height: 100%;
}

.templates-list::-webkit-scrollbar {
  width: 6px;
}

.templates-list::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

.template-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    background-color 0.2s ease,
    transform 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  position: relative;
}

.template-card:hover {
  border-color: #c7d2fe;
  box-shadow: 0 4px 14px rgba(62, 62, 254, 0.12);
  transform: translateY(-1px);
  background: #fafbff;
}

.template-card__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  flex-wrap: wrap;
}

.template-card__category {
  font-size: 10px;
  font-weight: 600;
  color: #3e3efe;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 3px 8px;
  background: #eef2ff;
  border-radius: 6px;
  flex-shrink: 0;
}

.template-card__name {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  margin: 0;
  line-height: 1.35;
  flex: 1;
  min-width: 0;
  word-break: break-word;
}

.template-card__subject {
  font-size: 12px;
  color: #374151;
  margin: 0;
  font-weight: 500;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.template-card__content {
  font-size: 11px;
  color: #6b7280;
  line-height: 1.55;
  margin: 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  max-height: calc(1.55em * 3);
}

.template-card__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 8px;
  margin-top: 2px;
  border-top: 1px solid #f3f4f6;
  gap: 8px;
  flex-wrap: wrap;
}

.template-card__stats {
  display: flex;
  gap: 6px;
  align-items: center;
}

.template-card__stat {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 500;
  border: none;
  padding: 4px 8px;
  border-radius: 6px;
  cursor: pointer;
  outline: none;
  transition:
    background-color 0.2s ease,
    transform 0.15s ease;
  background: #f3f4f6;
  color: #6b7280;
}

.template-card__stat--positive:hover {
  background: rgba(16, 185, 129, 0.1);
  color: #059669;
}

.template-card__stat--negative:hover {
  background: rgba(239, 68, 68, 0.08);
  color: #dc2626;
}

.template-card__stat:active {
  transform: scale(0.98);
}

.template-card__copy-btn {
  flex-shrink: 0;
}
</style>
